'use client'

import { createContext, useContext, useState, type ReactNode } from 'react'

import {
  Dialog,
  DialogContent,
  DialogTitle
} from '@/shared/ui/dialog'

type ModalOptions<TRecord extends object = object> = {
  modal: React.ComponentType<any>
  dontReplaceIfOpen?: boolean
  onHide?: () => void
  onSave?: (record: TRecord, opts?: { dontClose?: boolean }) => void
  dialogContentClassName?: string
  hideDialogCloseButton?: boolean
}

type ModalContextValue<
  TRecord extends object = object,
  TCtxProps extends object = object
> = {
  modalIsOpen: boolean
  openModal: (options: ModalOptions<TRecord>, contextProps?: TCtxProps) => void
  closeModal: () => void
} & TCtxProps

const ModalContext = createContext<ModalContextValue<any, any> | undefined>(undefined)

export function useModalContext<
  TRecord extends object = object,
  TCtxProps extends object = object
>(): ModalContextValue<TRecord, TCtxProps> {
  const ctx = useContext(
    ModalContext as React.Context<ModalContextValue<TRecord, TCtxProps> | undefined>
  )
  if (!ctx) throw new Error('useModalContext must be used within ModalProvider')
  return ctx
}

export function ModalProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false)
  const [options, setOptions] = useState<ModalOptions | null>(null)
  const [contextProps, setContextProps] = useState<object>({})

  const openModal = (modalOptions: ModalOptions, ctxProps: object = {}) => {
    if (modalOptions.dontReplaceIfOpen && open) return
    setOptions(modalOptions)
    setContextProps(ctxProps)
    setOpen(true)
  }

  const closeModal = () => {
    options?.onHide?.()
    setOpen(false)
    setOptions(null)
    setContextProps({})
  }

  return (
    <ModalContext.Provider value={{ ...contextProps, modalIsOpen: open, openModal, closeModal }}>
      {children}
      <Dialog open={open} onOpenChange={isOpen => { if (!isOpen) closeModal() }}>
        <DialogContent
          className={options?.dialogContentClassName}
          hideCloseButton={options?.hideDialogCloseButton}
        >
          <DialogTitle className="sr-only">Caixa de diálogo</DialogTitle>
          {options?.modal && (
            <options.modal
              {...contextProps}
              onHide={closeModal}
              onSave={(record: object, opts?: { dontClose?: boolean }) => {
                if (!opts?.dontClose) closeModal()
                options.onSave?.(record, opts)
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </ModalContext.Provider>
  )
}
